import React, { useState, useCallback, useMemo } from "react";
import { MotiView } from "moti";
import { View, StyleSheet } from "react-native";
import { PlusCircleIcon } from "@phosphor-icons/react";

import { useAppConfig } from "../../AppConfigProvider";
import {
  useGetAnnouncementsBySchoolQuery,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  ActivityState,
  type AnnouncementWithStatus,
} from "@axio/graphql/generated/graphql";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import AnnouncementFilters, {
  type ActivityFilter,
  type AudienceFilter,
} from "../../components/adminDashboard/Announcements/AnnouncementFilters";
import AnnouncementsList from "../../components/adminDashboard/Announcements/AnnouncementsList";
import AnnouncementModal from "../../components/adminDashboard/Announcements/AnnouncementModal";

function AdminAnnouncementsView() {
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>("all");
  const [targetAudience, setTargetAudience] = useState<AudienceFilter>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<AnnouncementWithStatus | null>(null);

  // Get school ID from app config
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  // GraphQL queries and mutations
  const {
    data: announcementsData,
    loading: announcementsLoading,
    refetch: refetchAnnouncements,
  } = useGetAnnouncementsBySchoolQuery({
    variables: { schoolId: schoolId || "", includeInactive: true },
    skip: !schoolId,
  });

  const [updateAnnouncement, { loading: updateLoading }] = useUpdateAnnouncementMutation();
  const [deleteAnnouncement, { loading: deleteLoading }] = useDeleteAnnouncementMutation();

  // Process announcements data
  const announcements = useMemo(() => {
    if (!announcementsData?.getAnnouncementsBySchool?.data) return [];
    return announcementsData.getAnnouncementsBySchool.data as AnnouncementWithStatus[];
  }, [announcementsData]);

  const loading = announcementsLoading || updateLoading || deleteLoading;

  const handleNewAnnouncement = useCallback(() => {
    setEditingAnnouncement(null); // Clear any editing state
    setShowCreateModal(true);
  }, []);

  const handleEditAnnouncement = (id: string) => {
    // Find the announcement by ID from real data
    const announcement = announcements.find((ann) => ann.id === id);
    if (announcement) {
      setEditingAnnouncement(announcement);
      setShowCreateModal(true);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    try {
      await deleteAnnouncement({
        variables: { id },
      });

      // Refetch announcements to update the list
      await refetchAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      // TODO: Show error message to user
    }
  };

  const handlePublishAnnouncement = async (id: string) => {
    try {
      // Find the announcement to get current data
      const announcement = announcements.find((ann) => ann.id === id);
      if (!announcement) return;

      await updateAnnouncement({
        variables: {
          id,
          input: {
            title: announcement.title,
            message: announcement.message,
            expirationDate: announcement.expirationDate,
            audienceType: announcement.audienceType,
            schoolId: schoolId,
            courseIds: announcement.courses?.map((c) => c.id) || [],
            degreeIds: announcement.degrees?.map((d) => d.id) || [],
            activityState: ActivityState.Published,
          },
        },
      });

      // Refetch announcements to update the list
      await refetchAnnouncements();
    } catch (error) {
      console.error("Error publishing announcement:", error);
      // TODO: Show error message to user
    }
  };

  const handleRescheduleAnnouncement = async (id: string) => {
    // This would open the edit modal with the announcement data
    handleEditAnnouncement(id);
  };

  const handleFilterChange = useCallback((filter: ActivityFilter) => {
    setActiveFilter(filter);
  }, []);

  const handleTargetAudienceChange = useCallback((audience: AudienceFilter) => {
    setTargetAudience(audience);
  }, []);

  return (
    <AdminLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomText
              text="Announcements"
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={styles.title}
            />
            <CustomSpacing size="s" />
            <CustomText text="Create and manage school-wide announcements and notifications" style={styles.subtitle} />
          </View>

          <View style={styles.headerRight}>
            <CustomButton
              text="New Announcement"
              leftIcon={<PlusCircleIcon size={20} weight="bold" color="#FFFFFF" />}
              onPress={handleNewAnnouncement}
              style={styles.newButton}
              textStyle={styles.newButtonText}
            />
          </View>
        </View>

        {/* Filter and Status Tabs */}
        <AnnouncementFilters
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          targetAudience={targetAudience}
          onTargetAudienceChange={handleTargetAudienceChange}
        />

        {/* Announcements List */}
        <AnnouncementsList
          announcements={announcements}
          activeFilter={activeFilter}
          targetAudience={targetAudience}
          onEdit={handleEditAnnouncement}
          onDelete={handleDeleteAnnouncement}
          onPublish={handlePublishAnnouncement}
          onReschedule={handleRescheduleAnnouncement}
          onCreateNew={handleNewAnnouncement}
        />

        {/* Announcement Modal */}
        <AnnouncementModal
          visible={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAnnouncement(null);
          }}
          schoolId={schoolId} // Pass schoolId for school-wide announcements
          announcement={editingAnnouncement}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingAnnouncement(null);
            // Refetch announcements to update the list
            refetchAnnouncements();
          }}
        />
      </MotiView>
    </AdminLayout>
  );
}

export default AdminAnnouncementsView;

const styles = StyleSheet.create({
  header: {
    marginBottom: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    color: "#101828",
  },
  subtitle: {
    color: "#667085",
  },
  newButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  newButtonText: {
    color: "#FFFFFF",
    fontWeight: "600",
  },
});
